import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Table, TableBody, TableHead, TableRow, TableCell, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box
} from '@mui/material';
import { UserBase } from '../../types/user';
import useUserApi from '../../api/useUserApi';
import { formatDateTime } from '../../utils/dateUtils';
import { renderGenderInHebrew } from '../../utils/textUtil';
import EditUser from './EditUser';
import { useNavigate } from 'react-router-dom';

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<UserBase[]>([]);
    const [open, setOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserBase | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<UserBase | null>(null);
    const { listUsers, deleteUser, createUser, updateUser } = useUserApi();
    const navigate = useNavigate();

    useEffect(() => {
        listUsers().then((users) => {
            // Filter out admin@horoscope.com from the list
            const filteredUsers = users.filter(user => user.email !== 'admin@horoscope.com');
            setUsers(filteredUsers);
        });
    }, [listUsers]);

    const handleOpenDialog = (user?: UserBase) => {
        setEditingUser(user || null);
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setEditingUser(null);
    };

    const handleSaveUser = async (userData: Partial<UserBase> & { password?: string }) => {
        try {
            if (editingUser) {
                // Edit existing user
                await updateUser(editingUser.id!, userData);
                setUsers((prev) =>
                    prev.map((u) => 
                        u.id === editingUser.id ? {
                            ...u,
                            ...userData
                        } : u
                    )
                );
            } else {
                // Add new user
                if (!userData.password || !userData.first_name || !userData.last_name || !userData.birth_date || !userData.gender) {
                    throw new Error('Required fields are missing');
                }
                const newUser = await createUser({
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    birth_date: userData.birth_date,
                    gender: userData.gender,
                    confirm_marketing_content: userData.confirm_marketing_content ?? true,
                    is_admin: userData.is_admin ?? false,
                    email: userData.email,
                    mobile: userData.mobile ?? '',
                    birth_time: userData.birth_time,
                    country_of_birth: userData.country_of_birth ?? '',
                    city_of_birth: userData.city_of_birth ?? '',
                    password: userData.password,
                    id: undefined,
                });
                setUsers((prev) => [...prev, newUser]);
            }
            setOpen(false);
            setEditingUser(null);
        } catch (error) {
            console.error('Error saving user:', error);
            // You might want to show an error message to the user here
        }
    };

    const handleDeleteUser = async (id: string) => {
        try {
            await deleteUser(id);
            setUsers((prev) => prev.filter((u) => u.id !== id));
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        } catch (error) {
            console.error('Error deleting user:', error);
            // You might want to show an error message to the user here
        }
    };

    const handleDeleteClick = (user: UserBase) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        };

    const handleHoroscopeClick = (user: UserBase) => {
        navigate(`/horoscope-management?userId=${user.id}`);
    };

    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                ניהול משתמשים
            </Typography>
            <Box mb={2}>
                <Button variant="contained" onClick={() => handleOpenDialog()}>
                    הוסף משתמש
                </Button>
            </Box>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="right">פעולות</TableCell>
                        <TableCell align="right">שם מלא</TableCell>
                        <TableCell align="center">אימייל</TableCell>
                        <TableCell align="center">טלפון</TableCell>
                        <TableCell align="center">תאריך לידה</TableCell>
                        <TableCell align="center">מגדר</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user.id}>
                            <TableCell align="right">
                                <Button onClick={() => handleOpenDialog(user)}>ערוך</Button>
                                <Button onClick={() => handleDeleteClick(user)}>מחק</Button>
                                <Button onClick={() => handleHoroscopeClick(user)}>פעילות</Button>
                            </TableCell>
                            <TableCell align="right">{user.first_name} {user.last_name}</TableCell>
                            <TableCell align="center">{user.email}</TableCell>
                            <TableCell align="center">{user.mobile}</TableCell>
                            <TableCell align="center">{formatDateTime(user.birth_date, user.birth_time)}</TableCell>
                            <TableCell align="center">{renderGenderInHebrew(user.gender)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <EditUser
                open={open}
                onClose={handleCloseDialog}
                onSave={handleSaveUser}
                editingUser={editingUser}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
                <DialogTitle>אישור מחיקה</DialogTitle>
                <DialogContent>
                    <Typography>
                        האם אתה בטוח שברצונך למחוק את המשתמש {userToDelete?.first_name} {userToDelete?.last_name}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>ביטול</Button>
                    <Button onClick={() => userToDelete?.id && handleDeleteUser(userToDelete.id)} color="error">
                        מחק
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UserManagement;
